import CardTrigger from '../../components/triggers/CardTrigger';
import { useTriggers } from '../../contexts/TriggerContext';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PerformanceDashboard from '../../components/PerformanceDashboard';
import SavedContent from '../../components/SavedContent';
import QuestionReview from '../../components/QuestionReview';
import LearningGoals from '../../components/LearningGoals';
import Achievements from '../../components/Achievements';
import './MetricsFeed.css';

const MetricsFeed = ({ userId }) => {
  const triggerCtx = useTriggers();
  const cardTriggers = triggerCtx ? triggerCtx.getTriggersByPlacement('metrics_feed') : [];
  const [activeTab, setActiveTab] = useState('dashboard');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMetrics();
  }, [userId]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/metrics/overview/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setMetrics(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load metrics');
      console.error('Error fetching metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'json') => {
    try {
      const response = await axios.get(
        `/api/metrics/export/${userId}?format=${format}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (format === 'json') {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        
        const exportFileDefaultName = `progress-report-${Date.now()}.json`;
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      } else if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `progress-report-${Date.now()}.csv`;
        link.click();
      }
    } catch (err) {
      console.error('Error exporting data:', err);
      alert('Failed to export data. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="metrics-feed loading">
        <div className="spinner"></div>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="metrics-feed error">
        <p>{error}</p>
        <button onClick={fetchMetrics} className="retry-btn">Try Again</button>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="metrics-feed error">
        <p>No metrics data available</p>
      </div>
    );
  }

  return (
    <div className="metrics-feed">
      <div className="metrics-header">
        <h2>Your Learning Metrics</h2>
        <div className="export-buttons">
          <button 
            onClick={() => handleExport('json')}
            className="export-btn"
          >
            Export JSON
          </button>
          <button 
            onClick={() => handleExport('csv')}
            className="export-btn"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="metrics-tabs">
        <button 
          className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button 
          className={`tab ${activeTab === 'review' ? 'active' : ''}`}
          onClick={() => setActiveTab('review')}
        >
          Review Questions
        </button>
        <button 
          className={`tab ${activeTab === 'saved' ? 'active' : ''}`}
          onClick={() => setActiveTab('saved')}
        >
          Saved Content
        </button>
        <button 
          className={`tab ${activeTab === 'goals' ? 'active' : ''}`}
          onClick={() => setActiveTab('goals')}
        >
          Learning Goals
        </button>
        <button 
          className={`tab ${activeTab === 'achievements' ? 'active' : ''}`}
          onClick={() => setActiveTab('achievements')}
        >
          Achievements
        </button>
      </div>

      <div className="metrics-content">
        {activeTab === 'dashboard' && (
          <PerformanceDashboard 
            metrics={metrics} 
            userId={userId}
            onRefresh={fetchMetrics}
          />
        )}
        {activeTab === 'review' && (
          <QuestionReview 
            userId={userId}
            progress={metrics.progress}
          />
        )}
        {activeTab === 'saved' && (
          <SavedContent 
            userId={userId}
            savedItems={metrics.user?.savedContent || []}
            onUpdate={fetchMetrics}
          />
        )}
        {activeTab === 'goals' && (
          <LearningGoals 
            userId={userId}
            goals={metrics.user?.learningGoals || []}
            onUpdate={fetchMetrics}
          />
        )}
        {activeTab === 'achievements' && (
          <Achievements 
            userId={userId}
            achievements={metrics.user?.metrics?.achievements || []}
            stats={metrics.stats}
            onUpdate={fetchMetrics}
          />
        )}
      </div>
    </div>
  );
};



export default MetricsFeed;